import { useState } from "react";
import { getPOIsAPI, getSinglePOIAPI, createPOIAPI, updatePOIAPI, deletePOIByIDAPI } from "../../services/poiAPI";
import { useLoading } from "../../contexts/LoadingContext";
import { useError } from "../../contexts/ErrorContext";
import { useSuccess } from "../../contexts/SuccessContext"
import { useUnsavedChanges } from "../../contexts/UnsavedChangesContext";
import { getPOIsByGroupAPI } from "../../services/poiGroupsAPI";
import { getUsersPOIsAPI, getUsersPOIGroupsAPI } from "../../services/poiGroupUsersAPI";

/**
 * Contains state for clickedPoints and lines, provides functionality to reset lines and clicked points, and functionality for interacting with API
 * 
 * @returns {ClickedPoint[]} clickedPoints
 * @returns {Function} setClickedPoints
 * @returns {Line[]} lines - all red lines on the map (lines that are not the currentLine)
 * @returns {Function} setLines 
 * @returns {Function} fetchPOIs - gets pois for a specific job (or poi group for that job) from the api
 * @returns {Function} savePOI - saves single poi to the api
 * @returns {Function} resetPOIs - resets state for lines and clickedPoints to empty arrays
 * @returns {Function} resetLines - see description below
 * @returns {Function} removeUnsavedClickedPoint - remove a clickedPoint from map that has not been saved yet
 * 
 * @category Hooks - POI Map
 * 
 */
const usePoiService = () => {
    const { showLoading, hideLoading } = useLoading();
    const { showError } = useError();
    const { setHasUnsavedChanges } = useUnsavedChanges();
    const { timeoutSuccess } = useSuccess();

    const [clickedPoints, setClickedPoints] = useState([]);
    const [lines, setLines] = useState([]);

    
    const getPOIsAPICall = async(jobId, selectedGroup) => {
        const page = window.location.pathname

        //if group selection was included, get pois by group
        if(selectedGroup){
            return await getPOIsByGroupAPI(selectedGroup.value)
        }

        //if there was no group selection, get pois by job
        //get pois for entire job if on poi-edit page, otherwise get the user's pois only
        if(page === "/poi-edit"){
            return await getPOIsAPI(jobId)
        }

        if(page.includes("/site-map")){
            return await getUsersPOIsAPI(jobId)
        }

        
    }

    /**
     * fetches all pois for job, or pois of a specific group if a group selection is provided
     * 
     * @param {number} jobId 
     * @param {DropdownOption} selectedGroup 
     */
    const fetchPOIs = async(jobId, selectedGroup = null) => {
        showLoading("Getting POIs...");
        setHasUnsavedChanges(false);

        if(selectedGroup !== null && selectedGroup.label.includes("Don't show POIs")){
            resetPOIs()
            hideLoading()
            return
        }

        const poiResponse = await getPOIsAPICall(jobId, selectedGroup)
        const userGroupResponse = await getUsersPOIGroupsAPI(jobId)

        if (!poiResponse.success || !userGroupResponse.success) {
            hideLoading();
            showError(`Error getting POIs: ${poiResponse.message}`);
        } else {
            const pois = poiResponse.data.pois ?? poiResponse.data //for group request, data is divided into group details and pois

            if (pois.length > 0) {
                let poiLines = [];
                let markerPoints = [];

                pois.forEach(poi => {
                    let lineID = Math.random() * 10000;

                    let linesObj = {
                        start: { 
                            lat: poi.Click_Point_Lat, 
                            lng: poi.Click_Point_Lng,
                            POIID: poi.POIID, 
                            POI_Name: poi.POI_Name,
                            lineID
                        },
                        end: { lat: poi.Intersect_Point_Lat, lng: poi.Intersect_Point_Lng },    
                    }

                    poiLines.push(linesObj);

                    //compare user groups with poi groups
                    //want to show only the poi groups the user is in on the dialog box
                    const usersGroups = userGroupResponse.data
                    const poiGroups = poi.POIGroups

                    const usersGroupsForPOI = usersGroups.filter(userGroup => 
                        poiGroups.some(poiGroup => poiGroup.POIGroupID === userGroup.POIGroupID)
                    );

                    let markerObj = {
                        lat: poi.Click_Point_Lat, 
                        lng: poi.Click_Point_Lng, 
                        POIID: poi.POIID, 
                        POI_Name: poi.POI_Name,
                        Description: poi.Description,
                        lineID,
                        poiGroups: usersGroupsForPOI,
                        CreatedBy: poi.CreatedBy,
                        linkedPOIs: poi.linkedPOIs,

                        //these fields will only be in poi if pois were fetched by group
                        ...(poi.AltPOI_Name && { AltPOI_Name: poi.AltPOI_Name }),
                        ...(poi.AltDescription && { AltDescription: poi.AltDescription }),
                        ...(poi.AltCreatedBy && { AltCreatedBy: poi.AltCreatedBy }),
                        ...(poi.LPOIID && { LPOIID: poi.LPOIID })
                    }
                    markerPoints.push(markerObj);
                });

                setLines(poiLines);
                setClickedPoints(markerPoints);
            } else {
                resetPOIs();
            }
            hideLoading();
        }
    }

    /**
     * fetches one POI for job, as well as the user's groups
     * 
     * @param {number} poiId 
     * @param {number} jobId 
     */
    const fetchSinglePOI = async(poiId, jobId, poiGroupId = null) => {

        showLoading("Getting POI...");
        setHasUnsavedChanges(false);

        const poiResponse = await getSinglePOIAPI(poiId, poiGroupId)
        const userGroupResponse = await getUsersPOIGroupsAPI(jobId)

        if (!poiResponse.success || !userGroupResponse.success) {
            hideLoading();

            const errorMessage = poiResponse.message ?? userGroupResponse.message

            showError(`Error getting POI: ${errorMessage}`);
        } else {
            const poi = poiResponse.data

            let poiLines = [];
            let markerPoints = [];

            let lineID = Math.random() * 10000;

            let linesObj = {
                start: { 
                    lat: poi.Click_Point_Lat, 
                    lng: poi.Click_Point_Lng,
                    POIID: poi.POIID, 
                    POI_Name: poi.POI_Name,
                    lineID
                },
                end: { lat: poi.Intersect_Point_Lat, lng: poi.Intersect_Point_Lng },    
            }

            poiLines.push(linesObj);

            //compare user groups with poi groups
            //want to show only the poi groups the user is in on the dialog box
            const usersGroups = userGroupResponse.data
            const poiGroups = poi.POIGroups

            const usersGroupsForPOI = usersGroups.filter(userGroup => 
                poiGroups.some(poiGroup => poiGroup.POIGroupID === userGroup.POIGroupID)
            );

            let markerObj = {
                lat: poi.Click_Point_Lat, 
                lng: poi.Click_Point_Lng, 
                POIID: poi.POIID, 
                POI_Name: poi.POI_Name,
                Description: poi.Description,
                lineID,
                poiGroups: usersGroupsForPOI,
                CreatedBy: poi.CreatedBy,
                linkedPOIs: poi.linkedPOIs,

                //these fields will only be in poi if pois were fetched by group
                ...(poi.AltPOI_Name && { AltPOI_Name: poi.AltPOI_Name }),
                ...(poi.AltDescription && { AltDescription: poi.AltDescription }),
                ...(poi.AltCreatedBy && { AltCreatedBy: poi.AltCreatedBy }),
                ...(poi.LPOIID && { LPOIID: poi.LPOIID })
            }
            markerPoints.push(markerObj);

            setLines(poiLines);
            setClickedPoints(markerPoints);

            hideLoading();

            return {
                point: markerObj,
                lines: poiLines
            }
        }
    }


    const savePOI = async (poiData, isNew) => {
        const response = isNew ? await createPOIAPI(poiData) : await updatePOIAPI(poiData);
        if (!response.success) {
            showError(response.message);
        } 
        return response;
    };

    const deletePOI = async (poi) => {
        showLoading("Deleting...");

        const response = await deletePOIByIDAPI(poi)
        hideLoading();
        if (!response.success) {
            showError(response.message);
        } else {
            setHasUnsavedChanges(false)
            timeoutSuccess("POI Deleted")
        }
        return response;
    };

    const resetPOIs = () => {
        setLines([]);
        setClickedPoints([]);
    };

    //if clickedPoint not already saved (has no POIID), remove from state
    const removeUnsavedClickedPoint = (clickedPoint) => {
        if(!clickedPoint || clickedPoint.POIID) return
        
        setClickedPoints((prevPoints) => {
            let updatedArr = [...prevPoints];
            updatedArr.pop();
            return updatedArr;
        });
    }


    /**
     * 
     * @param {Object[]} lineToAdd either currentLine, or lineBeforeDrag (if draggable marker was dragged and user chose to cancel that action)
     * @param {number | null} currentPOIID POIID for current clicked point (if has already been saved in the db - null if POI is new and not saved yet)
     * 
     *  adds current line to lines, so it becomes red
     *  then reset lines on map
     */
    const resetLines = (polylineManager, lineToAdd, currentPOIID) => {

        if (lineToAdd && Object.keys(lineToAdd).length && (lineToAdd.start.POIID || currentPOIID)) {
        
            let lineObj = {
                start: {
                    ...lineToAdd.start,
                    POIID: lineToAdd.start.POIID ?? currentPOIID
                },
                end: lineToAdd.end,
            }

            //reset the lines to show as red on the map, then below we can use the new coordinates to create the new currentLine in green
            setLines((prevLines) => [...prevLines, lineObj]);

        }

        //remove currentPolyline from map instance
        polylineManager.clearCurrentPolyline()

        //clear and re-add all other lines
        polylineManager.clearPolylines()
        polylineManager.addPolylines(lines)
    }

    return { 
        clickedPoints, 
        setClickedPoints, 
        lines, 
        setLines, 
        fetchPOIs,
        fetchSinglePOI,
        savePOI,
        deletePOI, 
        resetPOIs, 
        resetLines, 
        removeUnsavedClickedPoint 
    };
};

export default usePoiService;
